import { Component, EventEmitter, Input, OnDestroy, OnInit, Output } from '@angular/core';
import { AbstractControl, FormControl } from '@angular/forms';
import { Subject } from 'rxjs';
import { debounceTime, distinctUntilChanged, takeUntil } from 'rxjs/operators';
import { MedicineService } from 'src/core/services/medicine.service';
import { PackageType } from 'src/interfaces/medicine';

@Component({
  selector: 'core-package-type-search-field',
  templateUrl: './package-type-search-field.component.html',
  styleUrls: ['./package-type-search-field.component.scss'],
  standalone: false
})
export class PackageTypeSearchFieldComponent implements OnInit, OnDestroy {

  @Input({ required: true }) control!: AbstractControl;
  @Output() packageTypeCreated = new EventEmitter<PackageType>();

  packageTypes: PackageType[] = [];
  isSearching = false;

  private readonly destroy$ = new Subject<void>();
  private readonly searchLimit = 50;

  constructor(private _medicineService: MedicineService) { }

  ngOnInit(): void {
    this.loadPackageTypes('');

    const currentValue = this.control.value as PackageType | string | null;
    if (currentValue && typeof currentValue === 'object') {
      this.ensureSelectedInList(currentValue);
    }

    this.control.valueChanges
      .pipe(
        debounceTime(300),
        distinctUntilChanged(),
        takeUntil(this.destroy$)
      )
      .subscribe((value: PackageType | string | null) => {
        if (typeof value === 'string') {
          this.loadPackageTypes(value);
        } else if (value && typeof value === 'object') {
          this.ensureSelectedInList(value);
        }
      });
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  get formControl(): FormControl {
    return this.control as FormControl;
  }

  displayPackageType = (type: PackageType | string | null): string => {
    if (!type) {
      return '';
    }

    return typeof type === 'string' ? type : type.type;
  };

  onSearchInput(event: Event): void {
    const inputValue = (event.target as HTMLInputElement).value;
    const currentValue = this.control.value as PackageType | string | null;

    if (!currentValue || typeof currentValue === 'object') {
      this.control.setValue(inputValue, { emitEvent: true });
    }
  }

  async loadPackageTypes(search: string): Promise<void> {
    this.isSearching = true;

    try {
      this.packageTypes = await this._medicineService.getPackageTypes(1, this.searchLimit, search);

      const currentValue = this.control.value as PackageType | string | null;
      if (currentValue && typeof currentValue === 'object') {
        this.ensureSelectedInList(currentValue);
      }
    } finally {
      this.isSearching = false;
    }
  }

  async createPackageType(event: Event): Promise<void> {
    event.stopPropagation();

    const newPackageType: PackageType = await this._medicineService
      .openPackageTypeCreateDialog(
        'New package type',
        'Create new package type'
      );

    if (!newPackageType) {
      return;
    }

    this.ensureSelectedInList(newPackageType);
    this.control.setValue(newPackageType);
    this.packageTypeCreated.emit(newPackageType);
  }

  private ensureSelectedInList(packageType: PackageType): void {
    if (!this.packageTypes.some(type => type.id === packageType.id)) {
      this.packageTypes = [packageType, ...this.packageTypes];
    }
  }

}
